(function () {
  'use strict';

  /*
   * 2026-07-28 note on history.
   *
   * This file was rolled back to a pre-stepper revision at 07:30 by something
   * outside the editing session (CLI push or a version restore). Re-applied
   * afterwards. Two things were deliberately NOT restored:
   *
   *   - replaceHomepageProductImages() / homeProductImages. That feature swapped
   *     homepage product-card images for hardcoded CDN URLs and was the cause of
   *     511KB PNGs being downloaded into 110px tiles, plus it clobbered the
   *     header mega-menu. The rolled-back revision does not contain it, and the
   *     homepage is better off without it: the theme's own image_url sizing is
   *     already correct, with no JS mutating the DOM. Left out on purpose.
   *   - The 50ms __noodyDisableSubTimer observer, replaced below with one that
   *     only reacts to real element insertions and settles on a frame.
   */

  var productTextCache = new Map();
  var productFetches = new Map();
  var guardedForms = new WeakMap();
  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  var statusEl;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function ensureStatus() {
    if (statusEl) return statusEl;
    statusEl = document.createElement('div');
    statusEl.className = 'noody-atc-status';
    statusEl.setAttribute('role', 'status');
    statusEl.setAttribute('aria-live', 'polite');
    statusEl.setAttribute('aria-atomic', 'true');
    document.body.appendChild(statusEl);
    return statusEl;
  }

  function announce(message) {
    ready(function () {
      var el = ensureStatus();
      el.textContent = '';
      window.setTimeout(function () {
        el.textContent = message;
      }, 20);
    });
  }

  function normalizeUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (input.url) return input.url;
    return String(input);
  }

  function productHandleFromUrl(url) {
    var match = String(url).match(/\/products\/([^/?#]+)\.js(?:[?#].*)?$/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function productUrl(handle) {
    return '/products/' + encodeURIComponent(handle) + '.js';
  }

  function responseFromText(text) {
    if (typeof Response === 'undefined') return null;
    return new Response(text, {
      status: 200,
      headers: { 'Content-Type': 'application/json; charset=utf-8' }
    });
  }

  function prefetchProduct(handle) {
    if (!originalFetch || !handle) return Promise.resolve(null);
    if (productTextCache.has(handle)) return Promise.resolve(productTextCache.get(handle));
    if (productFetches.has(handle)) return productFetches.get(handle);

    var request = originalFetch(productUrl(handle), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Product prefetch failed');
        return response.text();
      })
      .then(function (text) {
        productTextCache.set(handle, text);
        return text;
      })
      .catch(function () {
        return null;
      })
      .finally(function () {
        productFetches.delete(handle);
      });

    productFetches.set(handle, request);
    return request;
  }

  function installFetchPatch() {
    if (!originalFetch || window.__noodyAtcFetchPatched) return;
    window.__noodyAtcFetchPatched = true;

    window.fetch = function () {
      var args = Array.prototype.slice.call(arguments);
      var url = normalizeUrl(args[0]);
      var init = args[1] || {};
      var method = String(init.method || (args[0] && args[0].method) || 'GET').toUpperCase();
      var handle = method === 'GET' ? productHandleFromUrl(url) : '';

      if (handle && productTextCache.has(handle)) {
        var cachedResponse = responseFromText(productTextCache.get(handle));
        if (cachedResponse) return Promise.resolve(cachedResponse);
      }

      var isCartAdd = method !== 'GET' && /\/cart\/add(?:\.js)?(?:[?#].*)?$/.test(url);
      var started = isCartAdd && window.performance ? window.performance.now() : 0;

      return originalFetch.apply(null, args).then(function (response) {
        if (isCartAdd) {
          var detail = {
            ok: response.ok,
            duration: started ? Math.round(window.performance.now() - started) : null
          };
          document.dispatchEvent(new CustomEvent('noody:cart-add-complete', { detail: detail }));
          announce(response.ok ? 'Added to cart.' : 'Could not add to cart. Please try again.');
        }
        return response;
      }).catch(function (error) {
        if (isCartAdd) {
          document.dispatchEvent(new CustomEvent('noody:cart-add-complete', {
            detail: { ok: false, duration: null }
          }));
          announce('Could not add to cart. Please try again.');
        }
        throw error;
      });
    };
  }

  function setControlsBusy(root, busy) {
    var controls = root.querySelectorAll('button[type="submit"], [data-add-to-cart], [data-floating-add-to-cart]');
    controls.forEach(function (control) {
      control.setAttribute('aria-busy', busy ? 'true' : 'false');
      control.classList.toggle('noody-atc-is-busy', busy);
      if (control.tagName === 'BUTTON') control.disabled = busy;
    });
  }

  function installSubmitGuard() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;

      if (guardedForms.get(form)) {
        event.preventDefault();
        announce('Adding to cart. Please wait.');
        return;
      }

      guardedForms.set(form, true);
      setControlsBusy(form, true);
      announce('Adding to cart.');

      window.setTimeout(function () {
        guardedForms.delete(form);
        setControlsBusy(form, false);
      }, 6500);
    }, true);

    document.addEventListener('noody:cart-add-complete', function () {
      document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
        guardedForms.delete(form);
        setControlsBusy(form, false);
      });
    });
  }

  function enhanceControlLabels() {
    var selectors = [
      '[data-add-to-cart]',
      '[data-floating-add-to-cart]',
      '.pair-add',
      'form[action*="/cart/add"] button[type="submit"]'
    ].join(',');

    document.querySelectorAll(selectors).forEach(function (control) {
      var label = (control.textContent || '').replace(/\s+/g, ' ').trim();
      if (label && !control.getAttribute('aria-label')) control.setAttribute('aria-label', label);
      if (!control.getAttribute('type') && control.tagName === 'BUTTON' && !control.closest('form')) {
        control.setAttribute('type', 'button');
      }
    });
  }

  function installPrefetchListeners() {
    function prefetchFromEvent(event) {
      var target = event.target.closest && event.target.closest('[data-handle]');
      if (target) prefetchProduct(target.getAttribute('data-handle'));
    }

    ['pointerenter', 'focusin', 'touchstart'].forEach(function (eventName) {
      document.addEventListener(eventName, prefetchFromEvent, {
        capture: true,
        passive: eventName !== 'focusin'
      });
    });

    ready(function () {
      /* Eager-prefetch only the first few cards; hover/focus covers the rest. */
      var items = Array.prototype.slice.call(document.querySelectorAll('[data-handle]')).slice(0, 4);
      var run = function () {
        items.forEach(function (item) {
          prefetchProduct(item.getAttribute('data-handle'));
        });
      };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 2500 });
      } else {
        window.setTimeout(run, 1200);
      }
    });
  }

  function disableSubscriptionUi() {
    document.querySelectorAll('input[name="purchase_mode"][value="subscribe"]').forEach(function (input) {
      input.checked = false;
      input.disabled = true;
      var mode = input.closest('.mode');
      if (mode) {
        mode.hidden = true;
        mode.setAttribute('aria-hidden', 'true');
      }
    });

    document.querySelectorAll('input[name="purchase_mode"][value="onetime"]').forEach(function (input) {
      input.checked = true;
      var mode = input.closest('.mode');
      if (mode) mode.classList.add('selected');
    });

    document.querySelectorAll('input[name="selling_plan"]').forEach(function (input) {
      input.value = '';
      input.setAttribute('value', '');
    });

    document.querySelectorAll('[data-subscription-selectors] input, [data-subscription-selectors] select, #bwFreq').forEach(function (field) {
      field.disabled = true;
    });

    document.querySelectorAll([
      '[data-subscription-selectors]',
      '.product__subs',
      '.product__subs__plans',
      '.product__subs__description',
      '.subswitch',
      '.bw-sub-switch',
      '#bwFreq',
      '.appstle_subscription_wrapper',
      '.appstle_sub_widget',
      '[data-appstle-subscription-widget]',
      '[class*="appstle_subscription"]'
    ].join(',')).forEach(function (node) {
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
    });

    document.querySelectorAll('[data-bw-switch], [data-switch-line], .subpill button, .bw-sub-pill button').forEach(function (button) {
      button.disabled = true;
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
    });

    /* Flag a purchase-mode group that is down to a single choice, so the
       stylesheet can hide it (:has() fallback for older engines). */
    document.querySelectorAll('.modes').forEach(function (group) {
      var visible = Array.prototype.filter.call(group.querySelectorAll('.mode'), function (mode) {
        return !mode.hidden;
      });
      group.classList.toggle('noody-modes--single', visible.length < 2);
    });
  }

  /*
   * PDP quantity stepper + multi-buy ladder.
   *
   * The Brand World buy box ships with no quantity control at all: the form
   * carries only hidden fields and a submit button, so every add posts a
   * quantity of 1 and the store's per-product automatic discounts are
   * unreachable from the product page. Verified that the form does respect an
   * injected quantity input (qty 3 posted 3 units).
   *
   * SHOW_MULTIBUY_TIERS controls the savings chips underneath the stepper.
   *
   * Currently FALSE for the 3rd birthday sale. Shopify applies only one
   * automatic discount per order, so a sitewide 30% discount suppresses the
   * per-product Buy 2/3/4 discounts entirely - showing the ladder during the
   * sale would promise savings that never apply. Set it back to true once the
   * sitewide discount is switched off and the quantity discounts resume.
   *
   * Percentages are the real customerGets values from the Shopify discounts,
   * NOT the discount titles - the titles understate them (the discount named
   * "Buy 3 Calm Balm Save 10%" actually takes 15% off). Bedtime Bestie
   * genuinely has no buy-2 tier.
   */
  var SHOW_MULTIBUY_TIERS = false;

  var MULTIBUY_TIERS = {
    'calm-balm': [[2, 10], [3, 15], [4, 20]],
    'lotion-potion': [[2, 10], [3, 15], [4, 20]],
    'soft-suds': [[2, 10], [3, 15], [4, 20]],
    'sun-balm': [[2, 10], [3, 15], [4, 20]],
    'bedtime-bestie': [[3, 15], [4, 20]]
  };

  var MAX_QTY = 10;
  var atcUnitPrice = null;

  function currentProductHandle() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function moneyNodeIn(root) {
    if (!root || typeof document.createTreeWalker !== 'function') return null;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (/\$\s?[\d,]+\.\d{2}/.test(node.textContent)) return node;
    }
    return null;
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-NZ', {
        style: 'currency',
        currency: 'NZD',
        currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function syncAtcPrice(form, qty) {
    var button = form.querySelector('button[type="submit"], button.atc');
    if (!button) return;
    var node = moneyNodeIn(button);
    if (!node) return;
    var match = node.textContent.match(/\$\s?([\d,]+\.\d{2})/);
    if (!match) return;
    var shown = Math.round(parseFloat(match[1].replace(/,/g, '')) * 100);
    if (atcUnitPrice === null) atcUnitPrice = shown;
    var next = formatMoney(atcUnitPrice * qty);
    var updated = node.textContent.replace(/\$\s?[\d,]+\.\d{2}/, next);
    if (node.textContent !== updated) node.textContent = updated;
  }

  function renderTiers(container, tiers, qty) {
    container.innerHTML = '';
    tiers.forEach(function (tier, index) {
      var item = document.createElement('li');
      item.className = 'noody-tiers__item';
      item.textContent = 'Buy ' + tier[0] + ' save ' + tier[1] + '%';
      var isLast = index === tiers.length - 1;
      if (qty >= tier[0] && (isLast || qty < tiers[index + 1][0])) {
        item.classList.add('is-active');
      }
      container.appendChild(item);
    });
    var note = document.createElement('li');
    note.className = 'noody-tiers__note';
    note.textContent = 'Multi-buy savings apply automatically at checkout.';
    container.appendChild(note);
  }

  function installQuantityStepper() {
    var form = document.querySelector('form[action*="/cart/add"]');
    // Collection cards have their own one-click add-to-cart form; keep the
    // PDP quantity stepper out of those cards.
    if (!form || form.closest('.product-grid-item') || form.querySelector('[data-noody-qty]')) return;
    var anchor = form.querySelector('.buy-row') || form.querySelector('button[type="submit"]');
    if (!anchor) return;

    var handle = currentProductHandle();
    var tiers = SHOW_MULTIBUY_TIERS ? MULTIBUY_TIERS[handle] : null;

    var wrap = document.createElement('div');
    wrap.className = 'noody-qty';
    wrap.setAttribute('data-noody-qty', '');

    var label = document.createElement('label');
    label.className = 'noody-qty__label';
    label.setAttribute('for', 'noody-qty-input');
    label.textContent = 'Quantity';

    var control = document.createElement('div');
    control.className = 'noody-qty__control';

    var down = document.createElement('button');
    down.type = 'button';
    down.className = 'noody-qty__btn';
    down.setAttribute('aria-label', 'Decrease quantity');
    down.textContent = '−';

    var input = document.createElement('input');
    input.type = 'number';
    input.id = 'noody-qty-input';
    input.className = 'noody-qty__input';
    input.name = 'quantity';
    input.value = '1';
    input.min = '1';
    input.max = String(MAX_QTY);
    input.step = '1';
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('aria-label', 'Quantity');

    var up = document.createElement('button');
    up.type = 'button';
    up.className = 'noody-qty__btn';
    up.setAttribute('aria-label', 'Increase quantity');
    up.textContent = '+';

    control.appendChild(down);
    control.appendChild(input);
    control.appendChild(up);
    wrap.appendChild(label);
    wrap.appendChild(control);

    var tierList = null;
    if (tiers && tiers.length) {
      tierList = document.createElement('ul');
      tierList.className = 'noody-tiers';
      tierList.setAttribute('data-noody-tiers', '');
    }

    function currentQty() {
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < 1) value = 1;
      if (value > MAX_QTY) value = MAX_QTY;
      return value;
    }

    function sync() {
      var qty = currentQty();
      input.value = String(qty);
      down.disabled = qty <= 1;
      up.disabled = qty >= MAX_QTY;
      if (tierList) renderTiers(tierList, tiers, qty);
      syncAtcPrice(form, qty);
    }

    down.addEventListener('click', function () {
      input.value = String(currentQty() - 1);
      sync();
    });
    up.addEventListener('click', function () {
      input.value = String(currentQty() + 1);
      sync();
    });
    input.addEventListener('change', sync);
    input.addEventListener('blur', sync);

    anchor.parentNode.insertBefore(wrap, anchor);
    if (tierList) anchor.parentNode.insertBefore(tierList, anchor);
    sync();
  }

  installFetchPatch();
  installSubmitGuard();
  installPrefetchListeners();
  ready(enhanceControlLabels);
  ready(disableSubscriptionUi);
  ready(installQuantityStepper);
  document.addEventListener('shopify:section:load', function () {
    enhanceControlLabels();
    disableSubscriptionUi();
    installQuantityStepper();
  });

  if ('MutationObserver' in window) {
    ready(function () {
      var scheduled = false;
      var observer = new MutationObserver(function (records) {
        if (scheduled) return;
        /* Only react to real element insertions. Text-node churn from other
           scripts used to retrigger this on a 50ms timer. */
        var relevant = false;
        for (var i = 0; i < records.length && !relevant; i += 1) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j += 1) {
            if (added[j].nodeType === 1) { relevant = true; break; }
          }
        }
        if (!relevant) return;
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          disableSubscriptionUi();
          installQuantityStepper();
        });
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
